import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { Switch } from '@shared/ui/atoms/Switch';
import { agent } from '@shared/services/agent';
import { selectCurrentUser, updateCurrentUser } from '@shared/store/auth.duck';
import { failureNotification } from '@shared/store/notification.duck';
import { updateCompanySetting } from '../../../services/CompanyService';
import { ConfigurationSection, SpaceBetweenRow } from '../../StyleComponents';
import { OfficeInformationView } from '../../office-management-page-v2/data/OfficeManagement.model';
import { CheckinIpAddressStatus, CheckinIpStatusPill, IpValue } from '../../office-management-page-v2/components/CheckinIpStatus';
import { AutomaticCheckinActionSettings } from './AutomaticCheckinActionSettings';

// The guide is only on the test marketing site until the relaunched site goes live
const GUIDE_URL = 'https://test-marketing.team-today.com/support/desk-booking/automatic-office-check-in';

interface NetworkName {
  id: number;
  networkName: string;
  officeId: number;
  officeName: string | null;
}

interface IpAddress {
  id: number;
  ipAddress: string;
  status: CheckinIpAddressStatus;
  officeId: number | null;
  officeName: string | null;
}

interface CompanyAutomaticCheckin {
  networkNames: NetworkName[];
  ipAddresses: IpAddress[];
}

/** Every office's check-in network names and approved or pending IP addresses. Changes are made per office. */
export function CompanyAutomaticCheckinPanel() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const enabled = !!user?.companyEntity?.automaticOfficeCheckinEnabled;
  const [overview, setOverview] = useState<CompanyAutomaticCheckin>();
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Refreshes the current user so office settings show or hide their Automatic Check-in panel straight away
  const onEnabledChange = async (value: boolean) => {
    try {
      setIsSaving(true);
      await updateCompanySetting('AUTOMATIC_OFFICE_CHECKIN', value);
      dispatch(updateCurrentUser(true));
    } catch (e: any) {
      dispatch(failureNotification('Could not save this setting. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    agent.requests.get('/api/company/automatic-checkin')
      .then((data: CompanyAutomaticCheckin) => { if (!cancelled) setOverview(data); })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <HeadlineSmall style={{ marginBottom: 16 }}>{t('settings.automatic-checkin')}</HeadlineSmall>

      <InfoBox>
        <BodyRegular weight={600}>How it works</BodyRegular>
        <BodyRegular>
          Team Today records an office visit when a check-in arrives from one of your approved office IP addresses.
          Check-ins can come from your Wi-Fi system or from a script on your Windows computers.
        </BodyRegular>
        <BodyRegular>
          <a href={GUIDE_URL} target="_blank" rel="noopener noreferrer">Read the automatic check-in guide</a>
        </BodyRegular>
      </InfoBox>

      <ConfigurationSection>
        <SpaceBetweenRow style={{ marginBottom: 2 }}>
          <BodyLarge colour={Colours.blue}>Automatic office check-in</BodyLarge>
          {isSaving ? <SimpleSpinner size={32} /> : <Switch value={enabled} label="" onChange={onEnabledChange} />}
        </SpaceBetweenRow>
        <BodyRegular>
          When this is on, check-ins from your approved office IP addresses are recorded. When it is off, they are
          ignored. Team Today checks each address belongs to you or your internet provider before approving it.
        </BodyRegular>
      </ConfigurationSection>

      {enabled && <AutomaticCheckinActionSettings />}

      <SectionTitle weight={600}>Which office someone checks into</SectionTitle>
      <BodyRegular colour={Colours.darkGrey}>
        Each check-in is matched to an office by the network name the person is connected to. If the check-in has no
        network name, or you have not mapped any network names, it falls back to the office of the IP address the
        check-in came from. Once you have mapped a network name, check-ins from networks that are not listed below
        are ignored.
      </BodyRegular>
      {enabled && (
        <BodyRegular colour={Colours.darkGrey} style={{ marginTop: 8 }}>
          To add or remove network names and IP addresses, choose an office below, or open it
          from <Link to="/manage/offices">Offices and meeting rooms</Link> and choose Automatic Check-in.
        </BodyRegular>
      )}

      {!overview && !loadFailed && <LoadingSpinner hideBorder={true} />}
      {loadFailed && <BodyRegular colour={Colours.darkGrey}>Could not load your automatic check-in settings.</BodyRegular>}

      {overview && (
        <>
          <SectionTitle weight={600}>Network names</SectionTitle>
          {overview.networkNames.length === 0 ? (
            <BodyRegular colour={Colours.darkGrey}>No network names are mapped to an office yet.</BodyRegular>
          ) : (
            <Table>
              <thead>
                <tr><th>Network name</th><th>Office</th></tr>
              </thead>
              <tbody>
                {overview.networkNames.map(row => (
                  <tr key={row.id}>
                    <td><strong>{row.networkName}</strong></td>
                    <td><OfficeName officeId={row.officeId} officeName={row.officeName} linkable={enabled} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <SectionTitle weight={600}>Office IP addresses</SectionTitle>
          {overview.ipAddresses.length === 0 ? (
            <BodyRegular colour={Colours.darkGrey}>No IP addresses are approved or waiting for approval.</BodyRegular>
          ) : (
            <Table>
              <thead>
                <tr><th>IP address</th><th>Status</th><th>Office</th></tr>
              </thead>
              <tbody>
                {overview.ipAddresses.map(row => (
                  <tr key={row.id}>
                    <td><IpValue>{row.ipAddress}</IpValue></td>
                    <td><CheckinIpStatusPill status={row.status} /></td>
                    <td><OfficeName officeId={row.officeId} officeName={row.officeName} linkable={enabled} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </>
  );
}

// Office settings only show the Automatic Check-in panel once it is switched on for the company
function OfficeName(props: { officeId: number | null, officeName: string | null, linkable: boolean }) {
  if (props.officeId === null) return <Muted>No office</Muted>;
  if (!props.officeName) return <Muted>Deleted office</Muted>;
  if (!props.linkable) return <>{props.officeName}</>;
  return <Link to={`/manage/offices/${props.officeId}?panel=${OfficeInformationView.AutomaticCheckin}`}>{props.officeName}</Link>;
}

const InfoBox = styled.div`
  margin-bottom: 24px;
  padding: 16px 20px;
  background: ${Colours.blue05};
  border: 1px solid ${Colours.blue20};
  border-radius: 8px;
  p + p {
    margin-top: 8px;
  }
`;

const SectionTitle = styled(BodyRegular)`
  margin: 24px 0 12px 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: proxima-nova;
  font-size: 14px;
  th {
    text-align: left;
    font-weight: 600;
    color: ${Colours.darkGrey};
    padding: 8px 12px 8px 0;
    border-bottom: 1px solid ${Colours.lightGrey};
  }
  td {
    padding: 10px 12px 10px 0;
    border-bottom: 1px solid ${Colours.lightGrey};
    vertical-align: middle;
    word-break: break-word;
  }
`;

const Muted = styled.span`
  color: ${Colours.darkGrey};
`;
